import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { useWorkspaceProjectApplicationOptions } from './components/WorkspaceProjectApplicationFields'
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

type ExistingAiGateway = RestSchemas['Messages.AiGateway.ExistingAiGateway']

const formSchema = z
  .object({
    targetType: z.enum(['workspace', 'application', 'project']),
    targetId: z.number().min(1, 'Select a target'),
  })

type TLinkForm = z.infer<typeof formSchema>

type AiGatewayLinkFormProps = {
  gateway: ExistingAiGateway
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
}

export const AiGatewayLinkForm = ({ gateway, onSubmit, sheetTrigger }: AiGatewayLinkFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const { id: orgId } = useCurrentOrganisation()
  const { getAiGatewayApiListGateways, putAiGatewayApiAddLink, getSubscriptionsApiGetOrganisationWorkspacesSummary } =
    useRestClient()

  const { projectOptions, applicationOptions } = useWorkspaceProjectApplicationOptions(orgId, undefined)

  const { data: workspaces } = getSubscriptionsApiGetOrganisationWorkspacesSummary.useQuery({
    query: { orgId },
  })

  const workspaceOptions = useMemo(
    () =>
      workspaces?.map((ws) => ({
        value: ws.workspaceId ?? 0,
        label: `${ws.projectName ?? 'Project'} / ${ws.applicationName ?? 'App'}`,
        description: `Workspace ${ws.workspaceId}`,
      })) ?? [],
    [workspaces]
  )

  const invalidate = () =>
    getAiGatewayApiListGateways.invalidateQueries({
      parameters: { query: { orgId } },
    })

  const { mutate: addLink, isPending } = putAiGatewayApiAddLink.useMutation(undefined, {
    onSuccess: () => {
      invalidate()
      setSheetOpen(false)
      toast.success('Link added')
      onSubmit?.()
    },
    onError: (err) => {
      const message =
        err && typeof err === 'object' && 'error' in err && typeof (err as { error?: string }).error === 'string'
          ? (err as { error: string }).error
          : 'Failed to add link (target may already have a gateway)'
      toast.error(message)
    },
  })

  const submitHandler = useCallback(
    (data: TLinkForm) => {
      const body =
        data.targetType === 'workspace'
          ? { workspaceId: data.targetId, applicationId: null, projectId: null }
          : data.targetType === 'application'
            ? { applicationId: data.targetId, workspaceId: null, projectId: null }
            : { projectId: data.targetId, workspaceId: null, applicationId: null }

      if (gateway.id == null) {
        toast.error('Unable to add link: missing gateway ID')
        return
      }

      addLink({
        path: { gatewayId: gateway.id },
        body,
      })
    },
    [addLink, gateway.id]
  )

  if (isPending) return <FormComponentLoading />

  return (
    <SheetWithFormComponent
      title="Link AI Gateway"
      description="Attach this gateway to one application, project, or workspace. Each target can have only one gateway."
      trigger={sheetTrigger}
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <LinkFormFields
        submitHandler={submitHandler}
        workspaceOptions={workspaceOptions}
        applicationOptions={applicationOptions}
        projectOptions={projectOptions}
      />
    </SheetWithFormComponent>
  )
}

function LinkFormFields({
  submitHandler,
  workspaceOptions,
  applicationOptions,
  projectOptions,
}: {
  submitHandler: (data: TLinkForm) => void
  workspaceOptions: { value: number; label: string; description?: string }[]
  applicationOptions: { value: number; label: string; description?: string }[]
  projectOptions: { value: number; label: string; description?: string }[]
}) {
  const form = useForm<TLinkForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      targetType: 'workspace',
      targetId: 0,
    },
  })

  const targetType = form.watch('targetType')
  const targetOptions =
    targetType === 'workspace'
      ? workspaceOptions
      : targetType === 'application'
        ? applicationOptions
        : projectOptions

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitHandler)} className="flex flex-col gap-4">
        <SingleSelectComboboxField
          control={form.control}
          name="targetType"
          label="Link type"
          options={[
            { value: 'workspace', label: 'Workspace' },
            { value: 'application', label: 'Application' },
            { value: 'project', label: 'Project' },
          ]}
          allowClear={false}
          placeholder="Select type"
        />
        <SingleSelectComboboxField
          key={targetType}
          control={form.control}
          name="targetId"
          label={
            targetType === 'workspace'
              ? 'Workspace'
              : targetType === 'application'
                ? 'Application'
                : 'Project'
          }
          options={targetOptions}
          placeholder="Select target"
        />
        <Button type="submit" className="self-end">
          Link
        </Button>
      </form>
    </Form>
  )
}
